import { z } from 'zod';
import { WORK_ORDER_PRIORITIES, WORK_ORDER_STATUSES } from '../enums.js';
import { nonNegativeMoneySchema } from '../money.js';
import { dateOnlySchema, mailingAddressSchema, optionalText, text, uuidSchema } from './common.js';
export const workOrderStatusSchema = z.enum(WORK_ORDER_STATUSES);
export const workOrderPrioritySchema = z.enum(WORK_ORDER_PRIORITIES);
// ---------------------------------------------------------------------------
// Vendors
// ---------------------------------------------------------------------------
export const createVendorSchema = z.object({
    name: text(200),
    /** Free text — "Plumbing", "HVAC", "Locksmith"... trades vary too much to pin down. */
    trade: optionalText(100),
    email: z.string().trim().toLowerCase().email().max(320).nullish(),
    phone: optionalText(32),
    /** For a vendor that's a company ("Acme Plumbing"); `name` is the company's own name. */
    contactPerson: optionalText(200),
    /** Not Michigan-restricted, unlike a property address — a vendor can be based anywhere. */
    mailingAddress: mailingAddressSchema.nullish(),
    notes: optionalText(4000),
});
export const updateVendorSchema = createVendorSchema.partial();
export const vendorListQuerySchema = z.object({
    search: optionalText(200),
    includeArchived: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
// ---------------------------------------------------------------------------
// Work orders
// ---------------------------------------------------------------------------
export const createWorkOrderSchema = z.object({
    unitId: uuidSchema,
    /** Who was living there when it was reported. Context, not scope. */
    leaseId: uuidSchema.nullish(),
    title: text(200),
    description: optionalText(4000),
    priority: workOrderPrioritySchema.default('MEDIUM'),
    scheduledOn: dateOnlySchema.nullish(),
});
/**
 * `completedAt` is never here — it's server-derived from the transition into
 * COMPLETED (see the service), not client-supplied. A client that could set
 * it directly could also set it to a date in the future or leave it out of
 * sync with `status`.
 */
export const updateWorkOrderSchema = z.object({
    title: text(200).optional(),
    description: optionalText(4000),
    priority: workOrderPrioritySchema.optional(),
    status: workOrderStatusSchema.optional(),
    vendorId: uuidSchema.nullish(),
    scheduledOn: dateOnlySchema.nullish(),
});
export const addWorkOrderNoteSchema = z.object({
    note: text(4000),
});
/**
 * A separate route (`PATCH /api/work-orders/:id/cost`), not a field on
 * `updateWorkOrderSchema` — `maintenance:write-cost` is narrower than
 * `maintenance:write`, and `app.requireCapability` (actor-context.ts) is
 * route-level only, with no field-level check anywhere in this codebase.
 * Same shape `POST /api/work-orders/:id/notes` already takes for its own
 * (different) reason.
 */
export const setWorkOrderCostSchema = z.object({
    cost: nonNegativeMoneySchema.nullable(),
});
/**
 * A tenant's own maintenance request. Deliberately narrower than
 * `createWorkOrderSchema`: no `unitId` (derived server-side from the lease,
 * never client-supplied — a tenant only gets to name a lease they're on),
 * no `priority` (fixed at MEDIUM, staff triages), no `status`/`vendorId`.
 */
export const createTenantMaintenanceRequestSchema = z.object({
    leaseId: uuidSchema,
    title: text(200),
    description: optionalText(4000),
});
export const workOrderListQuerySchema = z.object({
    unitId: uuidSchema.optional(),
    vendorId: uuidSchema.optional(),
    propertyId: uuidSchema.optional(),
    status: workOrderStatusSchema.optional(),
    priority: workOrderPrioritySchema.optional(),
});
//# sourceMappingURL=maintenance.js.map